import React, { useState } from "react";
import axios from "axios";
import { MapPin, Clock, MessageCircle, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WHATSAPP_URL = "https://wa.me/527712141634";

export const Contact = () => {
  const { t } = useLang();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !message) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { name, email, phone, message });
      toast.success(t.contact.sentTitle, { description: t.contact.sentDesc });
      setName("");
      setEmail("");
      setPhone("");
      setMessage("");
    } catch {
      toast.error(t.booking.errorTitle, { description: t.booking.errorDesc });
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contact"
      className="py-24 md:py-32 bg-white"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-14">
        <div>
          <span className="inline-block text-xs uppercase tracking-[0.25em] font-semibold text-green-700">
            {t.contact.eyebrow}
          </span>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">
            {t.contact.title}
          </h2>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">{t.contact.desc}</p>

          <div className="mt-10 space-y-5">
            <div className="flex gap-4">
              <div className="h-11 w-11 rounded-xl bg-green-100 text-green-700 flex items-center justify-center flex-none">
                <MapPin className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <p className="font-display font-semibold text-slate-900">{t.contact.address}</p>
                <p className="text-sm text-slate-500 mt-0.5">TERAFIQUIRO</p>
                <a
                  href="https://www.google.com/maps/dir/?api=1&destination=Calle+El+Pipila+200+Colonia+Centro+Pachuca+Hidalgo"
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-green-700 hover:text-green-800 hover:gap-1.5 transition-all"
                  data-testid="contact-directions-link"
                >
                  Cómo llegar →
                </a>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="h-11 w-11 rounded-xl bg-green-100 text-green-700 flex items-center justify-center flex-none">
                <Clock className="h-5 w-5" />
              </div>
              <div>
                <p className="font-display font-semibold text-slate-900">{t.contact.hoursTitle}</p>
                <p className="text-sm text-slate-500 mt-0.5">{t.contact.hours}</p>
              </div>
            </div>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noreferrer"
              className="flex gap-4 group"
              data-testid="contact-whatsapp-link"
            >
              <div className="h-11 w-11 rounded-xl bg-[#25D366]/10 text-[#128C7E] flex items-center justify-center flex-none group-hover:bg-[#25D366]/20 transition-colors">
                <MessageCircle className="h-5 w-5" />
              </div>
              <div>
                <p className="font-display font-semibold text-slate-900 group-hover:text-green-700 transition-colors">{t.contact.whatsapp}</p>
                <p className="text-sm text-slate-500 mt-0.5">+52 771 214 1634 · +52 771 161 0609 · +52 771 129 0073</p>
              </div>
            </a>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="bg-green-50/40 border border-green-100 rounded-3xl p-6 md:p-8"
          data-testid="contact-form"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label className="text-sm font-medium text-slate-700">{t.contact.msgName}</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="mt-1.5 bg-white border-slate-200 focus-visible:ring-green-500"
                data-testid="contact-input-name"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700">{t.contact.msgPhone}</Label>
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="mt-1.5 bg-white border-slate-200 focus-visible:ring-green-500"
                data-testid="contact-input-phone"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-sm font-medium text-slate-700">{t.contact.msgEmail}</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1.5 bg-white border-slate-200 focus-visible:ring-green-500"
                data-testid="contact-input-email"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-sm font-medium text-slate-700">{t.contact.msgMessage}</Label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                rows={5}
                className="mt-1.5 bg-white border-slate-200 focus-visible:ring-green-500"
                data-testid="contact-input-message"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={sending}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-green-600 px-6 py-3.5 text-sm font-semibold text-white hover:bg-green-700 transition-colors disabled:opacity-60"
            data-testid="contact-submit"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {sending ? t.contact.sending : t.contact.send}
          </button>
        </form>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 mt-14">
        <div className="overflow-hidden rounded-3xl border border-slate-100 shadow-sm" data-testid="contact-map">
          <iframe
            title="Ubicación TERAFIQUIRO - Calle El Pípila 200, Colonia Centro, Pachuca, Hidalgo"
            src="https://www.google.com/maps?q=Calle%20El%20Pipila%20200%20Colonia%20Centro%20Pachuca%20Hidalgo&output=embed"
            width="100%"
            height="380"
            style={{ border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </div>
      </div>
    </section>
  );
};

export default Contact;
